"use strict";
import { BigNumber } from "@ethersproject/bignumber";
import { queryOptions } from "@tanstack/react-query";
import { Token } from "@uniswap/sdk-core";
import { NATIVE_CHAIN_ID } from "constants/tokens";
import ms from "ms";
import { WRAPPED_NATIVE_CURRENCY, nativeOnChain } from "uniswap/src/constants/tokens";
import {
  Chain,
  HistoryDuration,
  PriceSource,
  TokenStandard
} from "uniswap/src/data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { isUniverseChainId } from "uniswap/src/features/chains/types";
import {
  fromGraphQLChain,
  isBackendSupportedChain,
  toGraphQLChain,
  toSupportedChainId
} from "uniswap/src/features/chains/utils";
import { AVERAGE_L1_BLOCK_TIME_MS } from "uniswap/src/features/transactions/hooks/usePollingIntervalByChain";
import { getChainIdFromBackendChain, getChainIdFromChainUrlParam } from "utils/chainParams";
import { getNativeTokenDBAddress } from "utils/nativeTokens";
export var PollingInterval = ((PollingInterval2) => {
  PollingInterval2[PollingInterval2["Slow"] = ms(`5m`)] = "Slow";
  PollingInterval2[PollingInterval2["Normal"] = ms(`1m`)] = "Normal";
  PollingInterval2[PollingInterval2["Fast"] = AVERAGE_L1_BLOCK_TIME_MS] = "Fast";
  PollingInterval2[PollingInterval2["LightningMcQueen"] = ms(`3s`)] = "LightningMcQueen";
  return PollingInterval2;
})(PollingInterval || {});
export var TimePeriod = /* @__PURE__ */ ((TimePeriod2) => {
  TimePeriod2["HOUR"] = "H";
  TimePeriod2["DAY"] = "D";
  TimePeriod2["WEEK"] = "W";
  TimePeriod2["MONTH"] = "M";
  TimePeriod2["YEAR"] = "Y";
  return TimePeriod2;
})(TimePeriod || {});
export function toHistoryDuration(timePeriod) {
  switch (timePeriod) {
    case "H" /* HOUR */:
      return HistoryDuration.Hour;
    case "D" /* DAY */:
      return HistoryDuration.Day;
    case "W" /* WEEK */:
      return HistoryDuration.Week;
    case "M" /* MONTH */:
      return HistoryDuration.Month;
    case "Y" /* YEAR */:
      return HistoryDuration.Year;
  }
}
export function toContractInput(currency, fallback) {
  const supportedChainId = toSupportedChainId(currency.chainId);
  const chain = toGraphQLChain(supportedChainId ?? fallback);
  return { chain, address: currency.isToken ? currency.address : getNativeTokenDBAddress(chain) };
}
export function gqlToCurrency(token) {
  if (!token.chain) {
    return void 0;
  }
  const chainId = getChainIdFromChainUrlParam(token.chain.toLowerCase()) ?? getChainIdFromBackendChain(token.chain);
  if (!chainId) {
    return void 0;
  }
  if (token.standard === TokenStandard.Native || token.address === NATIVE_CHAIN_ID || !token.address) {
    return nativeOnChain(chainId);
  } else {
    return new Token(
      chainId,
      token.address,
      token.decimals ?? 18,
      token.symbol ?? void 0,
      token.name ?? token.project?.name ?? void 0,
      void 0,
      token.feeData?.buyFeeBps ? BigNumber.from(token.feeData.buyFeeBps) : void 0,
      token.feeData?.sellFeeBps ? BigNumber.from(token.feeData.sellFeeBps) : void 0
    );
  }
}
export function fiatOnRampToCurrency(forCurrency) {
  if (!isUniverseChainId(Number(forCurrency.chainId))) {
    return void 0;
  }
  const supportedChainId = Number(forCurrency.chainId);
  if (!forCurrency.address) {
    return nativeOnChain(supportedChainId);
  } else {
    return new Token(supportedChainId, forCurrency.address, 18, forCurrency.cryptoCurrencyCode, forCurrency.displayName);
  }
}
export function supportedChainIdFromGQLChain(chain) {
  return isBackendSupportedChain(chain) ? fromGraphQLChain(chain) ?? void 0 : void 0;
}
export function getTokenExploreURL({ tab, chainUrlParam }) {
  return `/explore/${tab}${chainUrlParam ? `/${chainUrlParam}` : ""}`;
}
export function getTokenDetailsURL({
  address,
  chain,
  chainUrlParam,
  inputAddress,
  outputAddress
}) {
  const chainName = chainUrlParam || chain?.toLowerCase() || Chain.Ethereum.toLowerCase();
  const tokenAddress = address ?? NATIVE_CHAIN_ID;
  const inputAddressSuffix = inputAddress ? `?inputCurrency=${inputAddress}` : "";
  const outputAddressSuffix = outputAddress ? `&outputCurrency=${outputAddress}` : "";
  return `/explore/tokens/${chainName}/${tokenAddress}${inputAddressSuffix}${outputAddressSuffix}`;
}
export function unwrapToken(chainId, token) {
  if (!token?.address) {
    return token;
  }
  const address = token.address.toLowerCase();
  const nativeAddress = WRAPPED_NATIVE_CURRENCY[chainId]?.address.toLowerCase();
  if (address !== nativeAddress) {
    return token;
  }
  const nativeToken = nativeOnChain(chainId);
  return {
    ...token,
    ...nativeToken,
    project: {
      ...token.project,
      name: nativeToken.name
    },
    address: NATIVE_CHAIN_ID,
    extensions: void 0
    // prevents marking cross-chain wrapped tokens as native
  };
}
const PROTOCOL_META = {
  [PriceSource.SubgraphV2]: {
    name: "v2",
    color: "$DEP_blue400",
    gradient: { start: "rgba(96, 123, 238, 0.20)", end: "rgba(55, 70, 136, 0.00)" }
  },
  [PriceSource.SubgraphV3]: {
    name: "v3",
    color: "$accent1",
    gradient: { start: "rgba(252, 116, 254, 0.20)", end: "rgba(252, 116, 254, 0.00)" }
  },
  [PriceSource.SubgraphV4]: {
    name: "v4",
    color: "$chain_137",
    gradient: { start: "rgba(96, 123, 238, 0.20)", end: "rgba(55, 70, 136, 0.00)" }
  }
  /* [PriceSource.UniswapX]: { name: 'UniswapX', color: purple } */
};
export function getProtocolColor(priceSource) {
  return PROTOCOL_META[priceSource].color;
}
export function getProtocolName(priceSource) {
  return PROTOCOL_META[priceSource].name;
}
export var OrderDirection = /* @__PURE__ */ ((OrderDirection2) => {
  OrderDirection2["Asc"] = "asc";
  OrderDirection2["Desc"] = "desc";
  return OrderDirection2;
})(OrderDirection || {});
export function apolloQueryOptions(options) {
  return queryOptions({
    ...options,
    staleTime: 0
  });
}
